import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Fade,
  Autocomplete,
  alpha
} from '@mui/material';
import * as Maticons from '@mui/icons-material';
import SortableTree, { addNodeUnderParent, removeNode, changeNodeAtPath } from 'react-sortable-tree';
import 'react-sortable-tree/style.css';
import FileExplorerTheme from 'react-sortable-tree-theme-file-explorer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { postRequest } from '../../globalhelper/helper';

const MenuManagement = ({ allMenusData, refreshData, AllRoles }) => {
  const [treeData, setTreeData] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentNode, setCurrentNode] = useState(null);
  const [currentPath, setCurrentPath] = useState(null);
  const [copiedNode, setCopiedNode] = useState(null);
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [pathToDelete, setPathToDelete] = useState(null);

  // Initial Data Processing
  useEffect(() => {
    if (Array.isArray(allMenusData) && allMenusData.length > 0) {
      let uniqueRoles = [...new Set(allMenusData.map(item => item.subtype))].filter(data => data);
      let roleDataofUPA = [];
      if (Array.isArray(AllRoles)) {
        roleDataofUPA = [...new Set(AllRoles.filter(name => name && !name.includes('ActivityCode-')))];
      }
      uniqueRoles = [...new Set([...uniqueRoles, ...roleDataofUPA])];
      setRoles(uniqueRoles);
      if (uniqueRoles.length > 0 && !selectedRole) {
        setSelectedRole(uniqueRoles[0]);
      }
    }
  }, [allMenusData, selectedRole]);


  // Transform Flat to Tree when selectedRole changes
  useEffect(() => {
    if (selectedRole && allMenusData) {
      const roleData = allMenusData?.find(item => item.subtype === selectedRole);
      console.log(roleData, allMenusData);

      if (roleData && roleData.jsonobj) {
        try {
          const parsed = typeof roleData.jsonobj === 'string' ? JSON.parse(roleData.jsonobj) : roleData.jsonobj;
          // react-sortable-tree needs 'title' and 'children'
          const transform = (items) => items.map(item => ({
            ...item,
            title: item.name,
            children: item.submenus ? transform(item.submenus) : [],
            expanded: true
          }));
          setTreeData(transform(Array.isArray(parsed) ? parsed : [parsed]));
        } catch (e) {
          console.error("Error parsing menu JSON", e);
          setTreeData([]);
        }
      } else {
        setTreeData([]);
      }
    }
  }, [selectedRole, allMenusData]);

  const handleSave = async () => {
    setIsSaveConfirmOpen(false);
    try {
      // Transform Tree back to expected format
      const untransform = (items) => items.map(({ title, children, expanded, ...rest }) => ({
        ...rest,
        submenus: children ? untransform(children) : []
      }));

      const finalJson = untransform(treeData);
      const payload = {
        subtype: selectedRole,
        jsonobj: JSON.stringify(finalJson)
      };
      let existingRole = allMenusData?.find(item => item.subtype === selectedRole);
      if (existingRole) {
        let res = await postRequest('api/admin/UpdateMenus', payload)
        if (res?.type === 'success') {
          refreshData();
        }
      } else {
        let res = await postRequest('api/admin/CreateNewMenus', payload)
        if (res?.type === 'success') {
          refreshData();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleEdit = (node, path) => {
    setCurrentNode({ ...node });
    setCurrentPath(path);
    setIsEditDialogOpen(true);
  };

  const updateNode = () => {
    const newTree = changeNodeAtPath({
      treeData,
      path: currentPath,
      getNodeKey: ({ treeIndex }) => treeIndex,
      newNode: { ...currentNode, title: currentNode.name }
    });
    setTreeData(newTree);
    setIsEditDialogOpen(false);
  };

  const addNewNode = (parentPath) => {
    const newNode = {
      name: 'New Menu',
      title: 'New Menu',
      url: '/',
      icon: 'List',
      node: 'New Menu',
      open: false,
      group: false,
      order: `TID${Date.now()}`,
      pagename: null,
      pagetype: 'dynamic',
      submenus: [],
      short_text: 'New Menu',
      dashboard_id: ''
    };

    if (parentPath === null) {
      setTreeData([...treeData, newNode]);
    } else {
      const newTree = addNodeUnderParent({
        treeData,
        parentKey: parentPath[parentPath.length - 1],
        expandParent: true,
        getNodeKey: ({ treeIndex }) => treeIndex,
        newNode: newNode,
      }).treeData;
      setTreeData(newTree);
    }
  };

  const handleCopy = (node) => {
    setCopiedNode({ ...node });
  };

  const handlePaste = (parentPath) => {
    if (!copiedNode) return;
    const newNode = { ...copiedNode, order: `TID${Date.now()}` };
    if (parentPath === null) {
      setTreeData([...treeData, newNode]);
    } else {
      const newTree = addNodeUnderParent({
        treeData,
        parentKey: parentPath[parentPath.length - 1],
        expandParent: true,
        getNodeKey: ({ treeIndex }) => treeIndex,
        newNode: newNode,
      }).treeData;
      setTreeData(newTree);
    }
  };

  const handleDeleteClick = (node, path) => {
    setNodeToDelete(node);
    setPathToDelete(path);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    setTreeData(removeNode({
      treeData,
      path: pathToDelete,
      getNodeKey: ({ treeIndex }) => treeIndex,
    }).treeData);
    setIsDeleteConfirmOpen(false);
    setNodeToDelete(null);
    setPathToDelete(null);
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Role Selection</InputLabel>
              <Select
                value={selectedRole}
                label="Role Selection"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                {roles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Add Root Menu">
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => addNewNode(null)}>
                Add Root
              </Button>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshData}>Refresh</Button>
            <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={() => setIsSaveConfirmOpen(true)}>Save Changes</Button>
          </Box>
        </Box>

        <Card sx={{ flexGrow: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ 
            height: '100%', 
            p: 0,
            '& .rst__rowWrapper': {
              height: '54px !important',
            },
            '& .rst__row': {
              height: '54px !important',
            },
            '& .rst__rowContents': {
              height: '54px !important',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'transparent !important',
              border: 'none !important',
              boxShadow: 'none !important'
            },
            '& .rst__expandButton, & .rst__collapseButton': {
              top: '50% !important',
              transform: 'translateY(-50%) !important',
              marginTop: '0 !important',
              left: '-28px !important',
              position: 'absolute',
              filter: 'grayscale(1)',
              opacity: 0.6,
              '&:hover': { opacity: 1 }
            },
            '& .rst__moveHandle': {
              height: '100%',
              display: 'flex',
              alignItems: 'center'
            },
            '& .rst__virtualScrollHolder': {
              paddingLeft: '20px'
            }
          }}>
            <SortableTree
              treeData={treeData}
              onChange={treeData => setTreeData(treeData)}
              theme={FileExplorerTheme}
              rowHeight={54}
              innerStyle={{ padding: '10px' }}
              generateNodeProps={({ node, path }) => {
                const IconComponent = Maticons[node.icon] || Maticons.List;
                const hasChildren = node.children && node.children.length > 0;
                const isGroup = node.group === true || hasChildren;
                const depth = path.length;

                return {
                  style: {
                    height: '54px',
                  },
                  title: (
                    <Box
                      className="node-container"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        width: '100%',
                        pr: 2,
                        '& .node-actions': { opacity: 1, display: 'flex', gap: 0.5 },
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isGroup ? '#1e40af' : '#64748b',
                        bgcolor: alpha(isGroup ? '#1e40af' : '#64748b', 0.1),
                        p: 0.8,
                        borderRadius: '6px'
                      }}>
                        <IconComponent sx={{ fontSize: 18 }} />
                      </Box>

                      <Box sx={{ flexGrow: 1, minWidth: 150 }}>
                        <Typography sx={{
                          fontWeight: isGroup ? 700 : 500,
                          color: isGroup ? '#1e293b' : '#334155',
                          fontSize: isGroup ? '0.95rem' : '0.9rem'
                        }}>
                          {node.name}
                        </Typography>
                      </Box>

                      <Box className="node-actions">
                        <Tooltip title="Add Submenu">
                          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); addNewNode(path); }}>
                            <AddIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="info" onClick={(e) => { e.stopPropagation(); handleEdit(node, path); }}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(node); }}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Paste Under">
                          <IconButton size="small" disabled={!copiedNode} onClick={(e) => { e.stopPropagation(); handlePaste(path); }}>
                            <ContentPasteIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(node, path); }}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ),
                };
              }}
            />
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Menu Item</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={currentNode?.name || ''}
                  onChange={(e) => setCurrentNode({ ...currentNode, name: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Node ID"
                  value={currentNode?.node || ''}
                  onChange={(e) => setCurrentNode({ ...currentNode, node: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="URL"
                  value={currentNode?.url || ''}
                  onChange={(e) => setCurrentNode({ ...currentNode, url: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Icon"
                  value={currentNode?.icon || ''}
                  onChange={(e) => setCurrentNode({ ...currentNode, icon: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Short Text"
                  value={currentNode?.short_text || ''}
                  onChange={(e) => setCurrentNode({ ...currentNode, short_text: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Dashboard ID"
                  value={currentNode?.dashboard_id || ''}
                  onChange={(e) => setCurrentNode({ ...currentNode, dashboard_id: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Is Group</InputLabel>
                  <Select
                    value={currentNode?.group || false}
                    label="Is Group"
                    onChange={(e) => setCurrentNode({ ...currentNode, group: e.target.value })}
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Is Open</InputLabel>
                  <Select
                    value={currentNode?.open || false}
                    label="Is Open"
                    onChange={(e) => setCurrentNode({ ...currentNode, open: e.target.value })}
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Page Type</InputLabel>
                  <Select
                    value={currentNode?.pagetype || 'dynamic'}
                    label="Page Type"
                    onChange={(e) => setCurrentNode({ ...currentNode, pagetype: e.target.value })}
                  >
                    <MenuItem value="dynamic">Dynamic</MenuItem>
                    <MenuItem value="fixed">Fixed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={updateNode}>Update</Button>
          </DialogActions>
        </Dialog>

        {/* Save Confirmation Dialog */}
        <Dialog open={isSaveConfirmOpen} onClose={() => setIsSaveConfirmOpen(false)}>
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to save these menu changes for <strong>{selectedRole}</strong>?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsSaveConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleSave}>Confirm Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete the menu item <strong>{nodeToDelete?.name}</strong>?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default MenuManagement;
